import React, { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';

import ScreenHeading from 'components/ScreenHeading';
import Banner from 'components/PersonalizedInterview/Banner';
import CreateInterview from 'components/PersonalizedInterview/CreateInterview';
import PersonalizedInterviewList from 'components/PersonalizedInterview/PersonalizedInterviewList';
import TabSwitch from 'components/TabSwitch';
import CreateCodingInterview from './CreateCodingInterview';
import CreateShortListTask from 'components/CreateShortListTask';
import CreateCaseMasterContest from './CreateCaseMasterContest';
import { CaseMasterPublishingSteps, TemplateType } from 'components/PersonalizedInterview/constant';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 76px)',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0px 0px 4px 0px #00000026',
    marginBottom: theme.spacing(4),
  },
  hoverButton: {
    '&:hover': {
      cursor: 'pointer',
      boxShadow: '0px 0px 4px 0px #00000026',
      borderRadius: 100,
    },
  },
}));

const BANNER_DATA = [
  {
    title: (
      <Typography variant="h3-semiBold" color="neutral.clr-700">
        Publish{' '}
        <Box component="span" color="primary.main">
          Next Round Mock
        </Box>{' '}
        in Batch for upcoming interview rounds...
      </Typography>
    ),
    subTitle:
      '"82% of candidates who practiced mock interviews reported feeling more prepared for real interviews (CareerPlug)"',
    img: 'https://assets.languify.in/images/message-chat-01.png',
  },
  {
    title: (
      <Typography variant="h3-semiBold" color="neutral.clr-700">
        Publish{' '}
        <Box component="span" color="primary.main">
          Coding Mock
        </Box>{' '}
        in Batch for upcoming interview rounds...
      </Typography>
    ),
    subTitle:
      '"82% of candidates who practiced mock interviews reported feeling more prepared for real interviews (CareerPlug)"',
    img: 'https://assets.languify.in/images/brackets-typing.png',
  },
  {
    title: (
      <Typography variant="h3-semiBold" color="neutral.clr-700">
        Shortlist students{' '}
        <Box component="span" color="primary.main">
          quickly
        </Box>{' '}
        and{' '}
        <Box component="span" color="primary.main">
          fairly
        </Box>{' '}
        using relevance scores
      </Typography>
    ),
    subTitle: [
      'Create a shortlisting task and assign it to students',
      'Students upload their resumes and generate a relevance score to share with you',
      'Use these scores to shortlist candidates with clarity and confidence',
    ],
    img: 'https://assets.languify.in/images/shortlistBanner.png',
  },
  {
    title: (
      <Typography variant="h3-semiBold" color="neutral.clr-700">
        Publish{' '}
        <Box component="span" color="primary.main">
          Case Master Mocks &amp; Contests
        </Box>{' '}
        Across Batches for Real-World Business Case Practice
      </Typography>
    ),
    subTitle:
      '"Business case practice sharpens analytical thinking and decision-making skills for future leaders."',
    img: 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com/images/caseBanner.png',
  },
];

const INITIAL_STATE = {
  refresh: false,
  activeTab: 0,
  openNextRound: false,
  nextMockStep: 0,
  openCodingMock: false,
  currentCodingStep: null,
  interviewType: null,
  templateType: TemplateType.MOCK,
  openShortList: false,
  jobRoleTask: false,
  openCaseMaster: false,
  currentCaseMasterStep: null,
};

const PersonalizedInterviewScreen = () => {
  const classes = useStyles();
  
  const [state, setState] = useState(INITIAL_STATE);

  const handleNextRoundClose = useCallback(() => {
    setState(prev => ({
      ...prev,
      openNextRound: false,
      nextMockStep: 0,
    }));
  }, []);

  const handleCodingMockClose = useCallback(() => {
    setState(prev => ({
      ...prev,
      openCodingMock: false,
      currentCodingStep: null,
    }));
  }, []);

  const handleShortListClose = useCallback(() => {
    setState(prev => ({
      ...prev,
      openShortList: false,
    }));
  }, []);

  const handleCaseMasterClose = useCallback(() => {
    setState(prev => ({
      ...prev,
      openCaseMaster: false,
      currentCaseMasterStep: null,
    }));
  }, []);

  const setRefresh = useCallback((refresh) => {
    setState(prev => ({ ...prev, refresh }));
  }, []);

  const setActiveTab = useCallback((activeTab) => {
    setState(prev => ({ ...prev, activeTab }));
  }, []);

  const bannerHandlers = {
    setOpenNextround: (openNextRound) => 
      setState(prev => ({ ...prev, openNextRound })),
    setOpenCodingMock: (openCodingMock) => 
      setState(prev => ({ ...prev, openCodingMock })),
    setInterviewType: (interviewType) => 
      setState(prev => ({ ...prev, interviewType })),
    setCurrentCodingStep: (currentCodingStep) => 
      setState(prev => ({ ...prev, currentCodingStep })),
    setTemplateType: (templateType) => 
      setState(prev => ({ ...prev, templateType })),
    setOpenShortList: (openShortList) =>
      setState(prev => ({ ...prev, openShortList })),
    setJobRoleTask: (jobRoleTask) =>
      setState(prev => ({ ...prev, jobRoleTask })),
    setOpenCaseMaster: (openCaseMaster) =>
      setState(prev => ({ ...prev, openCaseMaster })),
    setCurrentCaseMasterStep: (currentCaseMasterStep) =>
      setState(prev => ({ ...prev, currentCaseMasterStep })),
  };

  const createInterviewHandlers = {
    setnextStep: (nextMockStep) => 
      setState(prev => ({ ...prev, nextMockStep })),
  };

  const codingInterviewHandlers = {
    setCurrentStep: (currentCodingStep) => 
      setState(prev => ({ ...prev, currentCodingStep })),
    setInterviewType: (interviewType) => 
      setState(prev => ({ ...prev, interviewType })),
    setTemplateType: (templateType) => 
      setState(prev => ({ ...prev, templateType })),
  };

  return (
    <Box className={classes.root}>
      <ScreenHeading title="Mock Interview" id="mock-interview" />
      
      <Box className={classes.container}>
        <TabSwitch 
          activeTab={state.activeTab} 
          setActiveTab={setActiveTab} 
        />
        
        <Banner
          data={BANNER_DATA[state.activeTab]}
          activeTab={state.activeTab}
          {...bannerHandlers}
        />

        <CreateInterview
          refresh={state.refresh}
          setRefresh={setRefresh}
          open={state.openNextRound}
          onClose={handleNextRoundClose}
          nextStep={state.nextMockStep}
          {...createInterviewHandlers}
        />

        <CreateCodingInterview
          setRefresh={setRefresh}
          open={state.openCodingMock}
          onClose={handleCodingMockClose}
          currentStep={state.currentCodingStep}
          interviewType={state.interviewType}
          templateType={state.templateType}
          {...codingInterviewHandlers}
        />

        <CreateShortListTask
          jobRoleSpecific={state.jobRoleTask}
          open={state.openShortList}
          onClose={handleShortListClose}
          setRefresh={setRefresh}
        />

        <CreateCaseMasterContest
          open={state.openCaseMaster}
          onClose={handleCaseMasterClose}
          setRefresh={setRefresh}
          currentStep={state.currentCaseMasterStep}
          setCurrentStep={(step) => setState(prev => ({ ...prev, currentCaseMasterStep: step }))}
        />

        <PersonalizedInterviewList
          refresh={state.refresh}
          setRefresh={setRefresh}
          activeTab={state.activeTab}
        />
      </Box>
    </Box>
  );
};

export default PersonalizedInterviewScreen;